/**
 * The tenant's confidence cutoffs, mounted for every badge in the app.
 *
 * `tierFor` has always taken a `cutoffs` argument and no call site ever passed
 * one, because there was nowhere to read a scope's from. This is that source:
 * it resolves the caller's confidence ladder once and publishes the effective
 * `tier_cutoffs` through `ConfidenceCutoffsProvider`, so a tenant who tightened
 * HIGH to 0.99 sees their pills grade against 0.99 rather than the platform's
 * 0.9.
 *
 * Deliberately best-effort and silent. A failed or slow policy read must not
 * block the app or surface an error — every consumer degrades to the platform
 * defaults, which is exactly today's behaviour. Grading a badge slightly
 * generously for a moment is a far smaller harm than a blank page.
 *
 * The sibling of `DashboardScopeHints`, which does the same job for the *other*
 * tenancy axis: resolve one app-level fact once, publish it through a ui-core
 * provider, and keep the routing/config choices in the app rather than the
 * package.
 */
import * as React from "react";
import { ConfidenceCutoffsProvider } from "@authe/ui-core";
import type { TierCutoffs } from "@authe/fe-commons/evidence";
import { useEvidencePolicySource } from "@/api/adapters";

export function DashboardConfidenceCutoffs({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const source = useEvidencePolicySource();
  const [cutoffs, setCutoffs] = React.useState<Partial<TierCutoffs> | null>(null);

  React.useEffect(() => {
    let live = true;
    source.getLadder().then(
      (ladder) => {
        if (!live) return;
        const raw = (ladder.effective as Record<string, unknown> | undefined)?.[
          "tier_cutoffs"
        ];
        if (raw && typeof raw === "object") {
          const t = raw as Partial<TierCutoffs>;
          setCutoffs({
            high: typeof t.high === "number" ? t.high : undefined,
            medium: typeof t.medium === "number" ? t.medium : undefined,
          });
        }
      },
      () => {
        // Swallowed on purpose. See the module note: platform defaults are a
        // correct answer, and there is nothing a reader could do about a failed
        // policy read from a transaction page.
      },
    );
    return () => {
      live = false;
    };
  }, [source]);

  return (
    <ConfidenceCutoffsProvider cutoffs={cutoffs}>
      {children}
    </ConfidenceCutoffsProvider>
  );
}
